import { createStyleContext, Slot } from "../../../utils/createStyleContext";
import {
  AromaContextualIconVariantProps,
  aromaMessageBubble,
  AromaMessageBubbleVariantProps,
} from "@zenchef/styled-system/recipes";
import { Box, HStack, HTMLStyledProps } from "@zenchef/styled-system/jsx";
import { ElementRef, forwardRef } from "react";
import { IconName } from "@zenchef/icomoon/dist/aromaticons/types";
import ContextualIcon from "../../ContextualIcon/ContextualIcon";
import { Assign } from "@ark-ui/react";
import React from "react";

// Create the style context for MessageBubble
const { withProvider, withContext } = createStyleContext(aromaMessageBubble);

// Define the slots
export type MessageBubbleSlots = Slot<typeof aromaMessageBubble>;

// Create a slots map similar to the Combobox implementation
export const slotsMaps: Record<MessageBubbleSlots, MessageBubbleSlots> = {
  message: "message",
  icon: "icon",
};

// Define allowed color themes
export type MessageBubbleColorTheme = "primary" | "secondary" | "ai";

// Define the components with proper typing
export const MessageBubbleRoot = withProvider<
  HTMLDivElement,
  Assign<HTMLStyledProps<"div">, AromaMessageBubbleVariantProps>
>(Box, slotsMaps.message);

// Create an icon component that uses the context
export const MessageBubbleIcon = withContext<
  HTMLDivElement,
  Assign<HTMLStyledProps<"div">, React.ComponentProps<typeof ContextualIcon>>
>(ContextualIcon, slotsMaps.icon);

// Types for the components
export interface MessageBubbleRootProps
  extends Omit<AromaMessageBubbleVariantProps, "type"> {
  colorTheme: MessageBubbleColorTheme;
  rank?: "solo" | "first" | "middle" | "last";
}

export interface MessageBubbleContentProps {
  children: React.ReactNode;
  icon?: IconName;
  colorTheme: MessageBubbleColorTheme;
}

// Create the MessageBubbleContent component
const MessageBubbleContent = forwardRef<
  ElementRef<"div">,
  MessageBubbleContentProps
>(({ children, icon, colorTheme }, ref) => {
  // Map color theme to an appropriate color for the icon
  const iconColorTheme =
    colorTheme === "ai" ? "ai" : colorTheme === "primary" ? "brand" : "neutral";

  let iconHierarchy: "bold" | "bolder" | "subtlest";

  switch (iconColorTheme) {
    case "ai":
      iconHierarchy = "bolder";
      break;
    case "brand":
      iconHierarchy = "bold";
      break;
    case "neutral":
      iconHierarchy = "subtlest";
      break;
    default:
      iconHierarchy = "bold";
      break;
  }

  return (
    <HStack alignItems="center" padding="padding.2" ref={ref}>
      {icon && (
        <MessageBubbleIcon
          name={icon}
          hierarchy={iconHierarchy}
          colorTheme={
            iconColorTheme as Exclude<
              AromaContextualIconVariantProps["colorTheme"],
              "ai"
            >
          }
        />
      )}
      {children}
    </HStack>
  );
});

MessageBubbleContent.displayName = "MessageBubbleContent";

export { MessageBubbleContent };
